import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { SLICE_NAME } from "./types";

import OCRInput from "../../../domains/ReportComparison/OCRInput";
import OCRFile from "../../../domains/ReportComparison/OCRFile";
import SelectOptions from "../../../domains/SelectOptions";
import Toast from "../../../domains/Utilities/Toast";
import OCRResult, {
  ResultsCollection,
} from "../../../domains/ReportComparison/OCRResult";

interface OCRState {
  OCRInputs: Array<OCRInput>
  isSelectPagesRangesPopUpOpen: boolean
  isSelectOCRValueOpen: boolean
  OCRResults: ResultsCollection
  shouldShowOCRInputList: boolean
  OCRFiles: Array<OCRFile>
  OCROptions: Array<SelectOptions>
  selectedFile: null | OCRFile
  isOCRSubmissionLoading: boolean
  hasErrorSubmittingOCR: boolean
  OCRSubmittedSuccessfully: boolean
  componentIdToBeUpdated: string
  selectedValue: OCRResult | null
  isUncheckedOCRValuesAlertOpen: boolean
  errorMessage: string
  OCRStartSuccess: boolean
  OCRToastStatus: Toast
}

const initialState: OCRState = {
  OCRInputs: [],
  isSelectPagesRangesPopUpOpen: false,
  OCRFiles: [],
  OCROptions: [],
  selectedFile: null,
  shouldShowOCRInputList: false,
  isOCRSubmissionLoading: false,
  OCRResults: {},
  hasErrorSubmittingOCR: false,
  isSelectOCRValueOpen: false,
  componentIdToBeUpdated: "",
  OCRSubmittedSuccessfully: false,
  selectedValue: null,
  isUncheckedOCRValuesAlertOpen: false,
  errorMessage: "",
  OCRStartSuccess: false,
  OCRToastStatus: { isOpen: false, message: "", status: undefined },
};

export const OCRSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    setOCRInput: (
      state,
      action: PayloadAction<{ fileId: string; range: number[] }>,
    ) => {
      const foundInput = state.OCRInputs.find(
        input => input.fileId === action.payload.fileId,
      );

      const [beginPage, endPage] = action.payload.range;

      if (foundInput) {
        foundInput.path = formatPath(foundInput.path);
        foundInput.beginPage = beginPage;
        foundInput.endPage = endPage;
      } else {
        const newInput = {
          beginPage,
          endPage,
          fileId: action.payload.fileId,
          fileName: state.selectedFile?.fileName as string,
          path: formatPath(state.selectedFile?.path as string),
        };

        state.OCRInputs.push(newInput);
      }
    },
    removeOCRInput: (state, action: PayloadAction<string>) => {
      const updatedInputs = state.OCRInputs.filter(
        file => file.fileId !== action.payload,
      );
      state.OCRInputs = updatedInputs;
    },
    openSelecPagesRangesPopUpOpen: state => {
      state.isSelectPagesRangesPopUpOpen = true;
    },
    closeSelecPagesRangesPopUpOpen: state => {
      state.isSelectPagesRangesPopUpOpen = false;
    },
    openSelectOCRValue: (state, action: PayloadAction<string>) => {
      state.isSelectOCRValueOpen = true;
      state.componentIdToBeUpdated = action.payload;
    },
    closeSelectOCRValue: state => {
      state.isSelectOCRValueOpen = false;
      state.componentIdToBeUpdated = "";
    },
    setOCRFiles: (state, action: PayloadAction<Array<OCRFile>>) => {
      state.OCRFiles = action.payload;
    },
    setOCROptions: (state, action: PayloadAction<Array<SelectOptions>>) => {
      state.OCROptions = action.payload;
    },
    setSelectedOCRFile: (state, action: PayloadAction<OCRFile | null>) => {
      state.selectedFile = action.payload;
    },
    shouldShowOCRInputList: (state, action: PayloadAction<boolean>) => {
      state.shouldShowOCRInputList = action.payload;
    },
    submitOCRForm: state => {
      state.isOCRSubmissionLoading = true;
    },
    submitOCRFormSuccess: (state, action: PayloadAction<OCRResult[]>) => {
      const results: ResultsCollection = {};

      for (let i = 0; i < action.payload.length; i++) {
        const result: OCRResult = action.payload[i];
        if (result.componentId) {
          if (!results[result?.componentId]) {
            results[result?.componentId] = [result];
          } else {
            results[result?.componentId].push(result);
          }
        }
      }

      state.OCRResults = results;
      state.OCRSubmittedSuccessfully = true;
      state.isOCRSubmissionLoading = false;
      state.hasErrorSubmittingOCR = false;
      state.errorMessage = "";
    },
    submitOCRFormError: (state, action?: PayloadAction<string>) => {
      state.isOCRSubmissionLoading = false;
      state.OCRSubmittedSuccessfully = false;
      state.hasErrorSubmittingOCR = true;
      if (action?.payload) {
        state.errorMessage = action?.payload;
      }
    },
    setSelectedValue: (state, action: PayloadAction<OCRResult>) => {
      state.selectedValue = action.payload;
    },
    resetOCRResults: state => {
      state.OCRResults = initialState.OCRResults;
    },
    resetOCRInputs: state => {
      state.OCRInputs = [];
    },
    openCheckedOCRValuesAlert: state => {
      state.isUncheckedOCRValuesAlertOpen = true;
    },
    closeCheckedOCRValuesAlert: state => {
      state.isUncheckedOCRValuesAlertOpen = false;
    },
    setStartOCR: (state: OCRState, action: PayloadAction<boolean>) => {
      state.OCRStartSuccess = action.payload;
    },
    openOCRToast: (
      state: OCRState,
      action: PayloadAction<{
        status: "success" | "info" | "error" | undefined
        message?: string
      }>,
    ) => {
      state.OCRToastStatus.status = action.payload.status;
      state.OCRToastStatus.message = action.payload.message;
      state.OCRToastStatus.isOpen = true;
    },
    closeOCRToast: (state: OCRState) => {
      state.OCRToastStatus.isOpen = false;
      state.OCRToastStatus.status = undefined;
      state.OCRToastStatus.message = "";
    },
  },
});

export const {
  openSelecPagesRangesPopUpOpen,
  setOCRInput,
  closeSelecPagesRangesPopUpOpen,
  setOCRFiles,
  setOCROptions,
  setSelectedOCRFile,
  removeOCRInput,
  shouldShowOCRInputList,
  submitOCRForm,
  submitOCRFormSuccess,
  submitOCRFormError,
  openSelectOCRValue,
  closeSelectOCRValue,
  setSelectedValue,
  resetOCRResults,
  resetOCRInputs,
  openCheckedOCRValuesAlert,
  closeCheckedOCRValuesAlert,
  setStartOCR,
  openOCRToast,
  closeOCRToast,
} = OCRSlice.actions;

const formatPath = (path: string) => {
  return path;
};

export default OCRSlice.reducer;
